import {useState} from 'react';
import {
    Alert,
    Badge,
    Button,
    Code,
    Group,
    List,
    Modal,
    Stack,
    Text,
} from '@mantine/core';
import {
    AiOutlineAudit,
    AiOutlineCheckCircle,
    AiOutlineCloudServer,
    AiOutlineQuestionCircle,
} from 'react-icons/ai';
import {Application} from '../types';
import {
    FoundResource,
    IncompleteCheck,
    MarkApplicationDecommissionedResult,
} from '../services/ApplicationsService';

// FNP-1007: mark an application DECOMMISSIONED when its infrastructure is
// verifiably already gone.
//
// ═══ THIS IS NOT A GENTLER DECOMMISSION. IT IS THE OPPOSITE ACTION ═══
//
// Decommission DESTROYS infrastructure — the bucket, the distribution, the DNS
// record, both pipelines, both repositories — and takes 15-25 minutes.
// This destroys NOTHING. It asks the server to prove the infrastructure is
// already gone and, only if the proof holds, corrects the record to say so.
// The blast radius is one DynamoDB row.
//
// ⚠️ THE FAILURE MODE THIS UI IS SHAPED AGAINST: an admin reaching for this to
// avoid a slow teardown. That would orphan live infrastructure — the record is
// renamed and filed away while the stack keeps serving, with nothing left
// naming it. Strictly worse than the problem it solves. So the copy leads with
// "no resources are deleted", the button is neutral-coloured rather than the
// destructive red that would suggest equivalence with Decommission, and the
// action is framed throughout as a RECORDS CORRECTION, not a teardown.
//
// The admin cannot talk their way past this: the server sweeps stacks, buckets,
// repositories and pipelines and refuses unless all four listings COMPLETED and
// all four were empty. The UI cannot override that, and does not try to.
//
// ═══ THE TWO REFUSALS ARE DIFFERENT AND MUST LOOK DIFFERENT ═══
//
// From the console, "no record + real infrastructure" and "no record + no
// infrastructure" are indistinguishable. The sweep is the only thing that tells
// them apart, and this modal is where that lands for a human. So the two
// refusal shapes get two different renderings, because they imply two different
// next steps:
//
//   `found`            RED. Resources were PROVED to still exist. The record is
//                      correct and must NOT be marked. Next step: Decommission,
//                      to actually tear them down.
//
//   `incompleteChecks` YELLOW. A sweep could not complete, so it proved NOTHING
//                      either way. The resources may or may not exist. Next
//                      step: retry, or escalate the cause (an IAM denial, a
//                      throttle). Explicitly NOT "use Decommission".
//
// Collapsing these into one "it failed" message would destroy the distinction
// the entire backend was built to provide.

interface Props {
    record: Application;
    // Resolves with the structured outcome. A REFUSAL resolves normally with
    // `marked: false` — it does not throw — so the caller's promise settling is
    // not evidence of success.
    onMarkDecommissioned: () => Promise<MarkApplicationDecommissionedResult | undefined>;
    disabled?: boolean;
}

// The four sweeps the server runs, named so the admin knows what "verified"
// actually covered before they trust it.
const SWEPT_RESOURCES = [
    'CloudFormation stacks',
    'S3 buckets',
    'CodeCommit repositories',
    'CodePipelines',
];

// Why a check could not complete, in plain language. The server sends the enum;
// an admin should not have to decode ACCESS_DENIED to know whether to retry or
// to call someone.
const FAILURE_KIND_EXPLANATION: Record<string, string> = {
    ACCESS_DENIED: 'the platform was not permitted to look',
    THROTTLED: 'AWS rate-limited the request',
    TRUNCATED: 'the listing was too large to read completely',
    ERROR: 'the call failed',
};

const explainFailureKind = (kind?: string | null) =>
    (kind && FAILURE_KIND_EXPLANATION[kind]) ?? 'the check did not complete';

export const MarkDecommissionedButton = (
    {
        record,
        onMarkDecommissioned,
        disabled = false,
    }: Props) => {

    const [opened, setOpened] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<MarkApplicationDecommissionedResult | null>(null);

    const applicationName = (record.name as string) ?? '';

    // Refusals, kept as separate pieces of state all the way to the screen.
    const found: FoundResource[] = result?.found ?? [];
    const incompleteChecks: IncompleteCheck[] = result?.incompleteChecks ?? [];
    const refused = result !== null && !result.marked;
    const marked = result?.marked === true;

    const close = () => {
        setResult(null);
        setSubmitting(false);
        setOpened(false);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            // ⚠️ The modal stays OPEN on the response. A refusal is the most
            // information this flow ever produces and it is not reproducible by
            // clicking again — dismissing on submit would throw it away.
            const outcome = await onMarkDecommissioned();
            setResult(outcome ?? null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {/*
              * Deliberately NOT red, and deliberately not `variant='outline'`
              * beside Decommission's outlined red. This action deletes nothing;
              * dressing it as destructive would suggest the two buttons are
              * variations on one theme, which is the confusion to avoid.
              */}
            {/*
              * FNP-1585: paired with the rename on DecommissionButton. This
              * reads "Mark as already gone" rather than "Mark as
              * decommissioned" so that the two controls differ by their whole
              * phrase instead of by a suffix.
              *
              * "Already gone" is also the more honest description: the action's
              * precondition is that the infrastructure is absent, and the
              * platform sweeps to prove it before agreeing. The old label named
              * the resulting RECORD STATE, which is the one thing an operator
              * deciding between these two buttons does not yet know.
              */}
            <Button
                variant='default'
                leftSection={<AiOutlineAudit/>}
                disabled={disabled}
                onClick={() => setOpened(true)}
                data-testid='mark-decommissioned-button'
            >
                Mark as already gone
            </Button>

            <Modal
                opened={opened}
                onClose={close}
                title={`Mark ${applicationName} as decommissioned`}
                size='lg'
            >
                <Stack gap='md'>

                    {/* ── Before submitting: what this does, and what it is not ── */}
                    {result === null && (
                        <>
                            <Alert
                                variant='light'
                                color='blue'
                                icon={<AiOutlineAudit/>}
                                title='This corrects the record. It does not delete anything.'
                            >
                                <Text size='sm'>
                                    Nothing in AWS is touched by this action — no stack, bucket,
                                    repository or pipeline is deleted, disabled or modified. It
                                    only updates this application&apos;s record to match
                                    infrastructure that is <strong>already gone</strong>.
                                </Text>
                                <Text size='sm' mt='xs'>
                                    Use it for a record left behind after the infrastructure was
                                    removed by hand. If the infrastructure still exists, use{' '}
                                    <strong>Tear down infrastructure</strong> instead — that is the
                                    action that destroys it.
                                </Text>
                            </Alert>

                            <div>
                                <Text size='sm' fw={500} mb='xs'>
                                    What the platform checks first:
                                </Text>
                                <Text size='sm' c='dimmed' mb='xs'>
                                    The record is not marked on your say-so. The platform sweeps
                                    for anything still belonging to this application and refuses
                                    unless every check completes and every one comes back empty:
                                </Text>
                                <List size='sm' spacing={4}>
                                    {SWEPT_RESOURCES.map((r) => (
                                        <List.Item key={r}>{r}</List.Item>
                                    ))}
                                </List>
                            </div>

                            <div>
                                <Text size='sm' fw={500} mb='xs'>What will change:</Text>
                                <List size='sm' spacing='xs'>
                                    <List.Item>
                                        The record is renamed to{' '}
                                        <Code>{applicationName}-decommissioned-&lt;date&gt;</Code>,
                                        freeing the name <Code>{applicationName}</Code> for reuse.
                                    </List.Item>
                                    <List.Item>
                                        Its status becomes <Code>DECOMMISSIONED</Code>, recorded as
                                        verified-absent rather than torn down.
                                    </List.Item>
                                    <List.Item>
                                        <strong>No AWS resources are deleted.</strong>
                                    </List.Item>
                                </List>
                            </div>
                        </>
                    )}

                    {/* ══════════════ REFUSAL 1: RESOURCES STILL EXIST ══════════════
                      * RED. This is the dangerous outcome: the admin was about to
                      * file away a record for infrastructure that is still running.
                      * Every resource is named, because "something exists" is not
                      * actionable and "this bucket exists" is.
                      */}
                    {refused && found.length > 0 && (
                        <Alert
                            variant='light'
                            color='red'
                            icon={<AiOutlineCloudServer/>}
                            title='Infrastructure still exists — this record must not be marked'
                            data-testid='mark-decommissioned-found'
                        >
                            <Text size='sm'>
                                The sweep <strong>proved</strong> that {found.length}{' '}
                                {found.length === 1 ? 'resource is' : 'resources are'} still
                                present. The record is accurate as it stands, and marking it would
                                orphan live infrastructure — renaming the record while the
                                resources keep running, with nothing left naming them.
                            </Text>
                            <List size='sm' spacing='xs' mt='sm'>
                                {found.map((f) => (
                                    <List.Item key={`${f.resourceType}:${f.name}`}>
                                        <Group gap='xs' wrap='nowrap'>
                                            <Badge color='red' variant='light' size='sm'>
                                                {f.resourceType}
                                            </Badge>
                                            <Code>{f.name}</Code>
                                        </Group>
                                    </List.Item>
                                ))}
                            </List>
                            <Text size='sm' mt='sm'>
                                <strong>Next step:</strong> use{' '}
                                <strong>Tear down infrastructure</strong> to destroy these, or find
                                out what still owns them.
                            </Text>
                        </Alert>
                    )}

                    {/* ═════════ REFUSAL 2: THE CHECKS COULD NOT COMPLETE ═════════
                      * YELLOW, and worded around "unknown" rather than "found".
                      * Nothing was proved in either direction: a listing denied by
                      * IAM returns zero items and is indistinguishable from an
                      * empty account, which is exactly why the server fails closed.
                      * The next step is retry or escalate — NOT Decommission, since
                      * there may be nothing there to tear down.
                      */}
                    {refused && incompleteChecks.length > 0 && (
                        <Alert
                            variant='light'
                            color='yellow'
                            icon={<AiOutlineQuestionCircle/>}
                            title='Could not verify — nothing was proved either way'
                            data-testid='mark-decommissioned-incomplete'
                        >
                            <Text size='sm'>
                                {incompleteChecks.length}{' '}
                                {incompleteChecks.length === 1 ? 'check' : 'checks'} could not be
                                completed, so the platform does not know whether these resources
                                exist. It will not assume they are gone: a check that fails
                                returns nothing, which looks exactly like an empty account.
                            </Text>
                            <List size='sm' spacing='xs' mt='sm'>
                                {incompleteChecks.map((c) => (
                                    <List.Item key={`${c.resourceType}:${c.failureKind}`}>
                                        <Group gap='xs' wrap='nowrap'>
                                            <Badge color='yellow' variant='light' size='sm'>
                                                {c.failureKind ?? 'UNKNOWN'}
                                            </Badge>
                                            <Text size='sm' span>
                                                {c.resourceType} — {explainFailureKind(c.failureKind)}
                                            </Text>
                                        </Group>
                                        {c.failureReason && (
                                            <Text size='xs' c='dimmed' mt={2}>
                                                {c.failureReason}
                                            </Text>
                                        )}
                                    </List.Item>
                                ))}
                            </List>
                            <Text size='sm' mt='sm'>
                                <strong>Next step:</strong> retry once the cause is cleared, or
                                escalate it. Do not decommission on the strength of this —
                                it did not say the resources are there.
                            </Text>
                        </Alert>
                    )}

                    {/* A refusal with neither list populated still has a server reason. */}
                    {refused && found.length === 0 && incompleteChecks.length === 0 && (
                        <Alert
                            variant='light'
                            color='orange'
                            title='This record was not marked'
                            data-testid='mark-decommissioned-refused'
                        >
                            <Text size='sm'>
                                {result?.reason ?? 'The platform refused to mark this record.'}
                            </Text>
                        </Alert>
                    )}

                    {/* ── Success ── */}
                    {marked && (
                        <Alert
                            variant='light'
                            color='green'
                            icon={<AiOutlineCheckCircle/>}
                            title='Record marked as decommissioned'
                            data-testid='mark-decommissioned-success'
                        >
                            <Text size='sm'>
                                The sweep found no remaining infrastructure. This record is now{' '}
                                <Code>{result?.name}</Code>, so the name{' '}
                                <Code>{result?.originalName ?? applicationName}</Code> is available
                                again.
                            </Text>
                            <Text size='sm' mt='xs'>
                                No AWS resources were changed.
                            </Text>
                        </Alert>
                    )}

                    <Stack gap='xs'>
                        {/*
                          * Hidden once a verdict is in. Re-submitting on a `found`
                          * refusal cannot succeed — the resources are there — and
                          * offering the button again would read as "try harder".
                          */}
                        {result === null && (
                            <Button
                                color='blue'
                                loading={submitting}
                                onClick={submit}
                                data-testid='mark-decommissioned-confirm'
                            >
                                Check for infrastructure, then mark the record
                            </Button>
                        )}
                        <Button variant='subtle' onClick={close}>
                            {result === null ? 'Cancel' : 'Close'}
                        </Button>
                    </Stack>
                </Stack>
            </Modal>
        </>
    );
};
